import React from 'react';
import MyAccountCard from './MyAccountCard';

interface RecentTableColumn {
    key: string;
    header: React.ReactNode;
    style?: React.CSSProperties;
}

interface MyAccountRecentTablePanelProps<T> {
    title: string;
    showHeader?: boolean;
    loading: boolean;
    data: T[] | null;
    maxItems: number;
    skeletonColumns: number;
    skeletonRows?: number;
    containerClassName?: string;
    tableStyle?: React.CSSProperties;
    emptyMessage?: string | null;
    columns: RecentTableColumn[];
    renderRow: (item: T) => React.ReactNode;
    getRowKey: (item: T) => string | number;
}

const getSkeletonLineWidth = (columnIndex: number, rowIndex: number): string => {
    const widthMap = ['72%', '56%', '44%', '62%', '38%'];
    const baseWidth = widthMap[columnIndex] || '52%';

    if (rowIndex % 2 === 0) {
        return baseWidth;
    }

    const narrowerWidthMap = ['64%', '48%', '36%', '54%', '32%'];
    return narrowerWidthMap[columnIndex] || '46%';
};

function MyAccountRecentTablePanel<T>({
    title,
    showHeader = true,
    loading,
    data,
    maxItems,
    skeletonColumns,
    skeletonRows = 5,
    containerClassName = 'order-history',
    tableStyle,
    emptyMessage = null,
    columns,
    renderRow,
    getRowKey
}: MyAccountRecentTablePanelProps<T>) {
    const renderLoadingState = () => {
        const skeletonRowsToRender = Math.min(maxItems, skeletonRows);
        const skeletonColumnsToRender = columns.length > 0
            ? columns
            : Array.from({ length: Math.max(1, skeletonColumns) }, (_, index) => ({
                key: `skeleton-${index}`,
                header: '',
            }));

        return (
            <MyAccountCard
                slug={containerClassName}
                title={showHeader ? <span className="skeleton d-block" style={{ height: '24px', width: '42%' }} /> : undefined}
                contentClassName={containerClassName}
            >
                <table className="w-full" style={tableStyle}>
                    <thead>
                        <tr>
                            {skeletonColumnsToRender.map((column, columnIndex) => (
                                <th key={`skeleton-header-${column.key}-${columnIndex}`} style={column.style}>
                                    <div
                                        className="skeleton"
                                        style={{
                                            height: '12px',
                                            width: columnIndex === 0 ? '58%' : '42%',
                                            marginBottom: '0.25rem',
                                        }}
                                    />
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: skeletonRowsToRender }).map((_, rowIndex) => (
                            <tr key={`skeleton-row-${rowIndex}`}>
                                {skeletonColumnsToRender.map((column, columnIndex) => (
                                    <td key={`skeleton-cell-${rowIndex}-${column.key}-${columnIndex}`} style={column.style}>
                                        <div
                                            className="skeleton"
                                            style={{
                                                height: '18px',
                                                width: getSkeletonLineWidth(columnIndex, rowIndex),
                                            }}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </MyAccountCard>
        );
    };

    if (loading && data === null) {
        return renderLoadingState();
    }

    const displayItems = data || [];
    if (displayItems.length === 0) {
        if (emptyMessage === null) {
            return null;
        }

        return (
            <MyAccountCard slug={containerClassName} title={showHeader ? title : undefined} contentClassName={containerClassName}>
                <p>{emptyMessage}</p>
            </MyAccountCard>
        );
    }

    return (
        <MyAccountCard slug={containerClassName} title={showHeader ? title : undefined} contentClassName={containerClassName}>
            <table className="w-full" style={tableStyle}>
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th key={column.key} style={column.style}>
                                {column.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {displayItems.slice(0, maxItems).map((item) => (
                        <tr key={getRowKey(item)}>
                            {renderRow(item)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </MyAccountCard>
    );
}

export default MyAccountRecentTablePanel;
